import React, { useContext, useEffect, useState, } from "react"
import { navigate } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { getShopifyImage } from "gatsby-source-shopify"
import { Handbag, MagnifyingGlass } from "@phosphor-icons/react"

import Tag from "@components/common/Tag"
import QuickBuyContext from "@context/QuickBuyContext"
import { trimStorefrontId, productFormattedPrice } from "@utils"

import { StoreContext } from "@context/StoreContext"
import { sendGAEvent } from "@utils/analytics"
import { GA_EVENTS } from "@utils/analytics/constants"


// Still contemplating whether to install propTypes as it may possibly conflict with gatsby-image libraries' own propTypes

/** cardOptions 
// no-tag - hides tags (if available)
// no-wishlist - hides wishlist button 
// no-buttons - hides add-to-cart and quickbuy now buttons
**/ 

let activeCardRef = { deactivate: null }

const CollectionProductCard = ({data, collectionHandle = null, cardOptions = '', style = 'classic', cardStyles, width, height}) => {
    
    const [hovered, setHovered] = useState(false)
    const [isTouched, setIsTouched] = useState(false)
    const { setQuickBuyData, setAddToBagClicked } = useContext(QuickBuyContext)
    const { checkout } = React.useContext(StoreContext)

    const cardOpt = cardOptions.split(' ')

    const handleProductClick = (handle) => {
        if(hovered && !isTouched) { 
            // navigate(`/products/${data?.name}`)
            navigate(`/products/${handle}`);
        } else {
			sendGAEvent(GA_EVENTS.SELECT_CONTENT, {content_type: 'product_card', content_id: data?.id})
		}
    }

    const handleQuickBuy = () => {
        console.log("Quick Buy")
    }

    const handleMouseEnter = () => {
        setHovered(true)
    }

    const handleInitialTouch = () => {
        if (activeCardRef.deactivate) {
            activeCardRef.deactivate()
        }
        setIsTouched(true)
        activeCardRef.deactivate = () => setIsTouched(false)
    }

    const handleSecondTouch = (handle) => {
        navigate(`/products/${handle}`)
    }
    
    const isFinalSale = data?.title?.split(" / ")?.[1]?.includes("FINAL SALE");
    const isDiscounted = data?.priceRangeV2?.minVariantPrice?.amount !== data?.variants?.[0]?.compareAtPrice ? true : false;

    
    const originalImage = React.useMemo(() => {
        const imageSrc = data?.media?.[1]?.preview?.image?.src;

        if (!imageSrc) {
            //console.warn("Image source is undefined, falling back to placeholder or null.");
            return null; // Or return a placeholder image object here if needed
        }


        const image = {
            originalSrc: data?.media?.[0]?.preview?.image?.src,
            width: data?.media?.[0]?.preview?.image?.gatsbyImageData?.width,
            height: data?.media?.[0]?.preview?.image?.gatsbyImageData?.height,
        }
    
        return getShopifyImage({
            image: image,
            layout: "constrained",
            crop: "contain",
            width: width,
            height: height,
        });
    }, [data?.media?.[0]?.preview?.image?.src, width, height]);

    const hoveredImage = React.useMemo(() => {
        const imageSrc = data?.media?.[1]?.preview?.image?.src;
        
        if (!imageSrc) {
            //console.warn("Image source is undefined, falling back to placeholder or null.");
            return null; // Or return a placeholder image object here if needed
        }

        const image = {
            originalSrc: data?.media?.[1]?.preview?.image?.src,
            width: data?.media?.[1]?.preview?.image?.gatsbyImageData?.width,
            height: data?.media?.[1]?.preview?.image?.gatsbyImageData?.height,
        }
    
        return getShopifyImage({
            image: image,
            layout: "constrained",
            crop: "contain",
            width: width,
            height: height,
        });
    }, [data?.media?.[1]?.preview?.image?.src]);

    const hasComingSoon = data?.collections?.some(collection => collection?.handle === "coming-soon");
    
    const badgeTags = data?.tags?.filter(tag => tag.startsWith("badge_"));

    const calculateDiscount = (compareAtPrice, price) => {
        if (!compareAtPrice || compareAtPrice <= price) return 0;
        
        return Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
    }
    const isPreOrder = badgeTags?.includes("badge_pre-order");
    const isBratz = badgeTags?.includes("badge_BRATZ®");

    const customCovers = [
        "holly-top-agua-fresca.jpg",
        "kelly-bottoms-da-gioia.jpg",
        "maddy-top-palmera.jpg",
        "maxine-top-paradise-blooms.jpg",
        "mens-board-shorts-buon-appetito.jpg",
        "mens-swim-shorts-agua-fresca.jpg",
        "mens-swim-shorts-blue-crush.jpg",
        "mens-swim-shorts-il-pesce.jpg",
        "mens-swim-shorts-lovers-beach.jpg",
        "mens-swim-shorts-palmera.jpg",
        "mens-swim-shorts-paradise-blooms.jpg",
        "mila-top-buon-appetito.jpg",
        "quinn-top-agua-fresca.jpg",
        "simone-top-paradise-blooms.jpg",
        "vera-top-lovers-beach.jpg",
        "ariel-top-butter-yellow.jpg",
        "jasmine-top-il-pesce.jpg",
        "kelly-top-da-gioia.jpg",
        "lydia-top-blue-crush.jpg",
        "maxine-bottoms-paradise-blooms.jpg",
        "mens-board-shorts-coco-frio.jpg",
        "mens-board-shorts-st-tropez.jpg",
        "mens-swim-shorts-navy-blue.jpg",
        "parker-top-buon-appetito.jpg",
        "tessa-top-coco-frio.jpg",
        "vera-top-il-pesce.jpg",
        "parker-bottoms-buon-appetito.jpg"
    ];

    const matchedImage = customCovers.find(filename =>
        filename.includes(data.handle)
    );

    let customCoverImage = null;
    
    if(matchedImage) {
        const customCover = "https://cdn.shopify.com/s/files/1/1219/5720/files/" + matchedImage;

        const image = {
            originalSrc: customCover,
            width: data?.media?.[0]?.preview?.image?.gatsbyImageData?.width,
            height: data?.media?.[0]?.preview?.image?.gatsbyImageData?.height,
        }
    
        customCoverImage = getShopifyImage({
            image: image,
            layout: "constrained",
            crop: "contain",
            width: width,
            height: height,
        });
    }
    
    return (
        <React.Fragment>
            <div
                className={`relative group ${ style === "classic" ? "w-min" : 
                    cardStyles !== null && cardStyles} w-full`}
                onMouseEnter={() => handleMouseEnter()}
                onMouseLeave={() => setHovered(false)}
                onTouchStart={() => handleInitialTouch()}
            >
                <div className="w-full absolute px-2 pt-2 flex justify-between cursor-pointer z-10">
                {
                    /*!cardOpt.includes("no-tag") && (
                    <div className="flex">{data?.isNew && <Tag label={"NEW"} />}</div>
                )*/}

                {
                    isDiscounted ? 
                        <div className="flex"><Tag className={`text-xs md:text-sm`} label={`SAVE ${calculateDiscount(data?.variants?.[0]?.compareAtPrice, data?.priceRangeV2?.minVariantPrice?.amount)} %`} /></div>
                        : badgeTags?.length > 0 ? isPreOrder ? <div className="flex"><Tag className={`text-xs md:text-sm`} label={"Pre Order"} /></div> : isBratz ? <div className="flex"><Tag className={`text-xs md:text-sm`} label={"BRATZ®"} /></div> : <div className="flex"><Tag className={`text-xs md:text-sm`} label={badgeTags?.[0]?.replace("badge_", "")?.replace("-", " ")} /></div> : null
                }
                {/*!cardOpt.includes("no-wishlist") && (
                    <HeartIcon onClick={() => console.log("heart click")} />
                )*/}
                </div>
                <div
                    className={`relative cursor-pointer overflow-hidden w-full mb-1 ${
                        cardStyles !== null
                        ? cardStyles
                        : style === "compact"
                        ? "h-30"
                        : "h-[300px] md:h-[320px]"
                    }`}
                    aria-label="checked-product-details"
                    onClick={() => {handleProductClick(data?.handle)}}
                    onTouchStart={() => {if(isTouched) handleSecondTouch(data?.handle)}}
                >

                    <div className={`relative w-full`}
                        style={{height: `${height}px`, width: `${width}px`}}
                    >
                        {
                            matchedImage && collectionHandle == "the-ashley-kelly-edit-island-life" ? 
                                <GatsbyImage
                                    className={`absolute border-gray-200 w-full h-full object-cover transition-all duration-300 ease-out ${
                                        (hovered || isTouched) ? "opacity-0" : "opacity-100"
                                    } ${
                                        style === "classic"
                                            ? "productcard-classic"
                                            : style === "compact"
                                            ? "productcard-compact"
                                            : "productcard-classic"
                                    } border-gray-200 ${(hovered || isTouched) ? "border-2" : "border-0"}`}
                                    alt={data?.altText || data?.title}
                                    image={customCoverImage}
                                    style={{position: "absolute"}}
                                />
                                : 
                                <GatsbyImage
                                    className={`absolute border-gray-200 w-full h-full object-cover transition-all duration-300 ease-out ${
                                        (hovered || isTouched) ? "opacity-0" : "opacity-100"
                                    } ${
                                        style === "classic"
                                            ? "productcard-classic"
                                            : style === "compact"
                                            ? "productcard-compact"
                                            : "productcard-classic"
                                    } border-gray-200 ${(hovered || isTouched) ? "border-2" : "border-0"}`}
                                    alt={data?.altText || data?.title}
                                    image={originalImage}
                                    style={{position: "absolute"}}
                                />
                        }
                        

                        <GatsbyImage
                            className={`absolute border-gray-200 w-full h-full object-cover transition-all duration-300 ease-out ${
                                (hovered || isTouched) ? "opacity-100" : "opacity-[0.01]"
                            } ${
                                style === "classic"
                                    ? "productcard-classic"
                                    : style === "compact"
                                    ? "productcard-compact"
                                    : "productcard-classic"
                            } border-gray-200 ${(hovered || isTouched) ? "border-2" : "border-0"}`}
                            alt={data?.altText || data?.title}
                            image={hoveredImage}
                            style={{position: "absolute"}}
                        />
                    </div>



                {style !== "compact" && (
                    <div
                    className={`absolute w-full flex justify-center items-center ${
                        (hovered || isTouched) ? "bottom-0" : "-bottom-20"
                    } bg-black bg-opacity-30 h-16 left-0 text-white`}
                    >
                    <MagnifyingGlass size={32} weight="light" />
                    </div>
                )}
                </div>
                <div className={`${style === "compact" && "space-y-1"}`}>
					<div className="product-name">{data?.title?.split(" / ")[0]}</div>
					<div className="product-sub">{data?.title?.split(" / ")[1]?.replace("FINAL SALE", "")}</div>
					<section className="flex flex-wrap justify-start items-center mt-1 gap-x-2">
						{style === "compact" ? (
							<span className="product-price !text-sm">
								{
									checkout?.cost?.totalAmount?.currencyCode ?
										productFormattedPrice(
											checkout?.cost?.totalAmount?.currencyCode,
											data?.priceRangeV2?.minVariantPrice?.amount
										)
										: null
								}
							</span>
						) : (
						<>
							<span className="product-price">
								{
									checkout?.cost?.totalAmount?.currencyCode ?
										productFormattedPrice(
											checkout?.cost?.totalAmount?.currencyCode,
											data?.priceRangeV2?.minVariantPrice?.amount
										)
										: null
								}
							</span>
							{isDiscounted && (
								<span className="discounted">
									{
										checkout?.cost?.totalAmount?.currencyCode ?
											productFormattedPrice(
												checkout?.cost?.totalAmount?.currencyCode,
												data?.variants?.[0]?.compareAtPrice
											)
											: null
									}
								</span>
							)}
							{isFinalSale && (
							<>
								<span className="hidden md:block text-[1.5rem] font-normal text-[#D1D1D1]">
								|
								</span>
								<span className="text-danger font-medium basis-full md:basis-auto">FINAL SALE</span>
							</>
							)}
							{hasComingSoon && (
								<>
								<span className="hidden md:block md:text-[1.5rem] font-normal text-[#D1D1D1]">
								|
								</span>
								<span className="font-medium basis-full md:basis-auto">COMING SOON</span>
								</>
							)}
							
						</>
						)}
					</section>
                </div>
                {!cardOpt.includes("no-buttons") && !hasComingSoon && (
                    <section
                        className={`flex flex-nowrap justify-between gap-x-2 mt-2 ${
                        style === "classic" &&
                        `transform opacity-0 group-hover:opacity-100 transition-opacity ${isTouched && 'opacity-100'}`
                        }`}
                    >
                        {style !== "compact" ? (
                        <>
                            <button
                                className={`btn-primary flex-1 ${trimStorefrontId(data?.variants?.[0]?.storefrontId, "pid-")}`}
                                onClick={() => setQuickBuyData(data)}
                                aria-label="Quick Buy"
                                data-pid={trimStorefrontId(data?.variants?.[0]?.storefrontId, "pid-")}
                            >
                                Quick Buy
                            </button>
                            <button
                                className={`btn-outline ${trimStorefrontId(data?.variants?.[0]?.storefrontId, "pid-")}`}
                                onClick={() => setAddToBagClicked(data)}
                                aria-label="Quick Add to Bag"
                                data-pid={trimStorefrontId(data?.variants?.[0]?.storefrontId, "pid-")}
                            >
                                <Handbag size={16} weight="light" />
                            </button>
                        </>
                        ) : (
                        <>
                            <button
                                className={`btn-outline flex-1 ${trimStorefrontId(data?.variants?.[0]?.storefrontId, "pid-")}`}
                                onClick={() => handleQuickBuy()}
                                aria-label="Quick Add To Bag"
                                data-pid={trimStorefrontId(data?.variants?.[0]?.storefrontId, "pid-")}
                            >
                                Add To Bag
                            </button>
                        </>
                        )}
                    </section>
                )}
            </div>
        </React.Fragment>
    );
}

export default CollectionProductCard